import { BufferAttribute, BufferGeometry, DoubleSide, Ray, ShapeUtils, Vector2, Vector3 } from 'three';
import { MeshBVH } from 'three-mesh-bvh';
import { extractMesh } from '@modules/three/common/voxels';
import {
  mergePositionIndexGeometries,
  removeSmallComponents,
  weldMeshVertices,
} from './alignerMeshShell';
import { buildInsideVoxelGrid } from './levelSetOffset';
import { repairToManifold } from './seamFusion';

/**
 * The hollow built the way every slicer builds it — marching cubes on the
 * distance field — but with ONE deliberate difference: the cubes only ever
 * touch the INSIDE.
 *
 * The existing voxel path (exportHollowModelToSTL) runs the field through
 * `applySheetSlab` and marches the whole shell, so both surfaces come out
 * resampled. That is why a reprojection subsystem grew underneath it —
 * markSharpFaces, a feature-masked fit, the interproximal channels held out of
 * that fit — all of it there to put anatomy back that the cubes had just taken
 * off. Measured on the captured upper jaw: sharp features p95 0.190 mm raw,
 * 0.085 mm after the masked fit, against 0.031 mm on flat areas. Every bit of
 * that is damage repair on a surface we had no reason to touch.
 *
 * Here the outer sheet is passed through untouched — bit for bit the geometry
 * the caller handed in — and only the cavity is marched. There is nothing to
 * reproject, so none of that machinery is needed, and the resolution question
 * changes character completely: no foil ever touches the inside, so the cell
 * size only has to resolve the WALL (~3 cells across it), not the anatomy.
 *
 * What this buys over the fixed-topology level set (offsetShell's
 * `fit: 'levelset'`) is the thing fixed topology structurally cannot express.
 * Where the fused body is thinner than two walls the cavity does not exist,
 * and a per-vertex inner sheet has to fake that by parking its vertices on the
 * medial axis — two sheets landing on the same surface, interpenetrating,
 * reading back as inside-out patches and a wall depth that jumps between full
 * and half-the-local-thickness. Marching cubes simply emits nothing there. The
 * region closes into solid because it IS solid.
 *
 * ## Two shells, not one welded body
 *
 * The result is written the way PrusaSlicer writes a hollowed model: an OUTER
 * closed surface (the sheet plus a flat cap over its rim) and a separate,
 * inward-facing CAVITY surface inside it. Both are closed on their own, so the
 * body has zero open edges and the cavity subtracts, exactly as a negative
 * volume should.
 *
 * The alternative — one welded manifold, with the cavity cut open at the base
 * plane and a flat ring triangulated between the two rims — was built first
 * and abandoned on evidence. It is fine on a box. On the captured jaw the
 * outer rim arrives as a single SLIT loop (the gum strip is a topological
 * disk, so its border runs out along the buccal edge and back along the
 * lingual one) while the cavity presents fifteen, several of them degenerate
 * at 0.00 mm². Deciding which of those are contours and which are holes is a
 * full 2D boolean on marching-cubes-resolution polygons, and the even-odd
 * nesting got it wrong: 5049 edges left open. Closing the cavity in the FIELD
 * instead — the half-space is exact, so the cap lands on the plane — needs no
 * 2D reasoning at all and cannot get the nesting wrong, because there is no
 * nesting.
 *
 * The base still opens for drainage: the outer cap covers it with material and
 * the cavity's own cap subtracts the same region back out.
 */

export type VoxelInteriorOptions = {
  /** Wall thickness, mm — the cavity is the iso-surface at −thickness. */
  thicknessMm: number;
  /** Which way the open base faces. */
  opens: 'up' | 'down';
  /** The plane the outer sheet's rim sits on (the skirt returns it). */
  planeY: number;
  /**
   * Grid resolution, mm. Defaults to a quarter of the wall, floored at 0.15 —
   * about three cells across the wall, which is what marching cubes needs to
   * carry it robustly. Finer buys nothing here: fidelity on the inside is
   * worth nothing, and the cost is cubic.
   */
  cellMm?: number;
  /** Cavity components with fewer triangles than this are dropped as noise. */
  minComponentTriangles?: number;
};

export type VoxelInteriorStats = {
  cellMm: number;
  cells: number;
  /** Triangles marching cubes produced for the cavity. */
  cavityTriangles: number;
  /** Cavity components dropped as voxel noise. */
  bubblesRemoved: number;
  /** Cavity components dropped for sitting outside the model altogether. */
  straysRemoved: number;
  /** Marching-cubes cracks closed in the cavity. */
  cavityCracksFilled: number;
  /** Open edges the cavity still had after that — should be zero. */
  cavityOpenEdges: number;
  /** How much the cavity takes out, mm³. Negative would mean it adds. */
  cavityVolumeMm3: number;
  /** Whether the cavity had to be turned inside out to face the body. */
  cavityReversed: boolean;
  /** Border loops of the outer sheet found on the base plane. */
  rimLoops: number;
  /** Border loops of the outer sheet NOT on the base plane — these stay open. */
  strayLoops: number;
  /** Border loops the cavity presents on the base plane, once its lid is off. */
  cavityRimLoops: number;
  /** Triangles in the flat ring closing the base. */
  capTriangles: number;
  /**
   * Whether that cap ended up facing away from the body. False means the sheet
   * handed in is wound inside-out — the body will be too.
   */
  capFacesOutward: boolean;
  /**
   * Area the cavity opens in the base plane, mm². Near zero means the cavity
   * never reached the plane and the model is solid at the bottom.
   */
  cavityCapAreaMm2: number;
  /** Area of the ring itself, mm² — the material the model stands on. */
  capAreaMm2: number;
  /** One line per rim loop: vertex count, nesting depth, area. */
  loopReport: string[];
};

export type VoxelInteriorResult = {
  geometry: BufferGeometry;
  stats: VoxelInteriorStats;
};

/**
 * Builds the printable body: the caller's `outer` sheet, a flat cap closing
 * its rim, and the cavity marched from its distance field. `outer` is read,
 * never modified.
 *
 * Returns null when there is nothing to build — no rim on the base plane to
 * cap, or a body nowhere thicker than two walls so no cavity exists.
 */
export async function buildVoxelInteriorShell(
  outer: BufferGeometry,
  options: VoxelInteriorOptions,
): Promise<VoxelInteriorResult | null> {
  const thickness = options.thicknessMm;
  const { opens, planeY } = options;
  const cell = options.cellMm ?? Math.max(0.15, thickness / 4);

  // Raise a temporary COLLAR on the rim before the field is built, and seal
  // above THAT instead of on the base plane.
  //
  // Whatever seals the open base is a surface, and the field cannot tell a
  // seal from anatomy. With the plug sitting a fraction below the rim, the
  // nearest surface to everything just inside the base was the plug's own
  // underside — closer than a wall's thickness — so no cavity formed there and
  // the model came out with a solid floor across its whole footprint
  // (measured: 193 mm² of cavity reaching a 811 mm² base). Not a coplanar-face
  // artifact: real material.
  //
  // Extending the rim past where the model will be cut moves that seal out of
  // reach, so the cavity is bounded by the side walls alone and runs straight
  // through the base plane. The cut then opens it at full width. The collar is
  // for the field only and never reaches the output.
  const collarHeight = thickness + 4 * cell;
  const collar = extrudeRimCollar(outer, planeY, opens, collarHeight);
  const sealed = collar ? mergePositionIndexGeometries([outer, collar]) : outer;
  collar?.dispose();
  const { grid } = await buildInsideVoxelGrid(sealed, {
    thicknessMm: thickness,
    opens,
    cellMm: cell,
    planeY: opens === 'up' ? planeY + collarHeight : planeY - collarHeight,
  });
  if (sealed !== outer) sealed.dispose();

  // Shift the field so its zero is the cavity's surface, and clear everything
  // past the base plane, in one pass.
  //
  // The clearing does two jobs. It keeps the PLUG out of the result — the plug
  // is a solid box and its own cavity would otherwise march as a second, larger
  // component. And because the half-space term is exact where it dominates,
  // it is also what closes the cavity flat ON the plane: no mesh surgery, no
  // rim to stitch.
  const { origin, counts, strides, cellSize } = grid.info;
  const data = grid.data;
  for (let iy = 0; iy < counts.y; iy++) {
    const worldY = origin.y + iy * cellSize;
    // Positive on the side being removed, so the max() below erases it.
    const beyond = opens === 'up' ? worldY - planeY : planeY - worldY;
    for (let ix = 0; ix < counts.x; ix++) {
      let index = ix * strides.x + iy * strides.y;
      for (let iz = 0; iz < counts.z; iz++, index++) {
        const cavity = data[index] + thickness;
        data[index] = cavity > beyond ? cavity : beyond;
      }
    }
  }

  let marched: BufferGeometry;
  try {
    marched = extractMesh(grid, { isoValue: 0, invertSign: true, computeNormals: false });
  } catch {
    // The extractor throws rather than returning an empty mesh. Nothing at the
    // −thickness iso means the body is nowhere thicker than two walls.
    return null;
  }

  const cavityGeometry = weldMeshVertices(marched, cellSize * 1e-3);
  marched.dispose();
  const bubblesRemoved = removeSmallComponents(cavityGeometry, options.minComponentTriangles ?? 64);
  const straysRemoved = removeComponentsOutside(cavityGeometry, outer, planeY, opens, cell);

  // Classic marching cubes leaves cracks: the 256-case table resolves an
  // ambiguous face independently on each side of it, and where two neighbours
  // disagree the surface has a hole (measured on the box fixture, 2130 open
  // edges). Close them before anything reads the cavity as a solid.
  const cracks = repairToManifold(cavityGeometry, 0, 6);

  // Orient it by MEASUREMENT. A cavity is a void the body encloses, so the
  // body's outward normal on it points INTO it and its signed volume is
  // negative — it subtracts. Which way the extractor happens to wind its
  // output is then not something this has to know, and that matters: reasoning
  // about that convention from the tables gave the wrong answer twice, and the
  // symptom is silent — the surface looks right in a viewer either way, and
  // what breaks is the volume the slicer computes.
  const marchedVolume = signedVolume(cavityGeometry);
  const cavityReversed = marchedVolume > 0;
  if (cavityReversed) reverseWinding(cavityGeometry);

  const planeTolerance = Math.max(cellSize * 0.05, 1e-4);
  const outerVertexCount = (outer.getAttribute('position') as BufferAttribute).count;

  // Measure the cavity's flat lid but LEAVE IT ON. It is the base opening, and
  // detaching it to use its border as the ring's inner edge was tried and does
  // not survive this geometry: the lid arrives as eight loops, and one of them
  // (11 mm²) sits outside the sheet's rim polygon altogether, so no nesting
  // rule — single point or majority vote — can classify it. It was promoted to
  // its own contour and the base came back with 1969 open edges and more area
  // than the footprint has. Keeping the lid keeps the cavity a closed shell,
  // which is what makes the two-shell form work without any nesting at all.
  const cavityCapAreaMm2 = planarFaceArea(cavityGeometry, planeY, planeTolerance);

  const outerLoops = borderLoops(outer);
  const rims = onPlane(outer, outerLoops, planeY, planeTolerance);
  if (rims.length === 0) {
    cavityGeometry.dispose();
    return null;
  }

  const outerPosition = outer.getAttribute('position') as BufferAttribute;
  const loops: PlaneLoop[] = rims.map((loop) => ({
    polygon: loop.map((v) => new Vector2(outerPosition.getX(v), outerPosition.getZ(v))),
    ids: loop.slice(),
    depth: 0,
  }));

  // The ring faces away from the body: the model sits below the plane when it
  // opens up, and above it when it opens down.
  const cap = triangulateBaseRing(loops, opens === 'up' ? 1 : -1, (v) => [
    outerPosition.getX(v),
    outerPosition.getZ(v),
  ]);
  const geometry = concatenate(outer, cap.triangles, cavityGeometry);
  const cavityTriangles = cavityGeometry.getIndex()!.count / 3;
  cavityGeometry.dispose();

  return {
    geometry,
    stats: {
      cellMm: cellSize,
      cells: grid.info.total,
      cavityTriangles,
      bubblesRemoved,
      straysRemoved,
      cavityCracksFilled: cracks.loopsFilled,
      cavityOpenEdges: cracks.openEdgesAfter,
      cavityVolumeMm3: Math.abs(marchedVolume),
      cavityReversed,
      rimLoops: rims.length,
      strayLoops: outerLoops.length - rims.length,
      capTriangles: cap.triangles.length / 3,
      capFacesOutward: cap.facesOutward,
      cavityCapAreaMm2,
      cavityRimLoops: 0,
      capAreaMm2: cap.areaMm2,
      loopReport: cap.report,
    },
  };
}

/**
 * A vertical wall standing on every border edge of `sheet`, `heightMm` tall,
 * wound to continue the sheet. Null when the sheet has no border.
 *
 * Only the FIELD sees it. Its job is to carry the seal far enough past the
 * base plane that the seal's own surface stops being the nearest thing to the
 * material just inside the rim.
 */
function extrudeRimCollar(
  sheet: BufferGeometry,
  planeY: number,
  opens: 'up' | 'down',
  heightMm: number,
): BufferGeometry | null {
  const index = sheet.getIndex();
  const position = sheet.getAttribute('position') as BufferAttribute | null;
  if (!index || !position) return null;
  const tris = index.array as ArrayLike<number>;
  const vertexCount = position.count;
  const key = (a: number, b: number) => a * vertexCount + b;

  const directed = new Set<number>();
  for (let f = 0; f < tris.length; f += 3) {
    for (let e = 0; e < 3; e++) directed.add(key(tris[f + e], tris[f + ((e + 1) % 3)]));
  }
  const rise = opens === 'up' ? heightMm : -heightMm;
  const positions: number[] = [];
  const faces: number[] = [];
  const raised = new Map<number, number>();
  const at = (v: number, up: boolean) => {
    if (!up) {
      const id = positions.length / 3;
      positions.push(position.getX(v), position.getY(v), position.getZ(v));
      return id;
    }
    const known = raised.get(v);
    if (known !== undefined) return known;
    const id = positions.length / 3;
    positions.push(position.getX(v), position.getY(v) + rise, position.getZ(v));
    raised.set(v, id);
    return id;
  };

  for (let f = 0; f < tris.length; f += 3) {
    for (let e = 0; e < 3; e++) {
      const from = tris[f + e];
      const to = tris[f + ((e + 1) % 3)];
      if (directed.has(key(to, from))) continue;
      // The sheet walks from→to, so the wall continues it as to→from at the
      // bottom and the reverse at the top.
      const a = at(from, false);
      const b = at(to, false);
      const aUp = at(from, true);
      const bUp = at(to, true);
      faces.push(a, aUp, bUp, a, bUp, b);
    }
  }
  if (faces.length === 0) return null;

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
  geometry.setIndex(new BufferAttribute(new Uint32Array(faces), 1));
  return geometry;
}

/**
 * Ordered border loops — directed edges with no oppositely wound partner,
 * chained end to end. Kept as a multimap rather than one successor per vertex:
 * where two loops touch at a vertex it has two border edges leaving it, and a
 * plain map would silently drop one.
 */
export function borderLoops(geometry: BufferGeometry): number[][] {
  const index = geometry.getIndex();
  const position = geometry.getAttribute('position') as BufferAttribute | null;
  if (!index || !position) return [];
  const tris = index.array as ArrayLike<number>;
  const vertexCount = position.count;
  const key = (a: number, b: number) => a * vertexCount + b;

  const directed = new Set<number>();
  for (let f = 0; f < tris.length; f += 3) {
    for (let e = 0; e < 3; e++) directed.add(key(tris[f + e], tris[f + ((e + 1) % 3)]));
  }
  const outgoing = new Map<number, number[]>();
  for (let f = 0; f < tris.length; f += 3) {
    for (let e = 0; e < 3; e++) {
      const from = tris[f + e];
      const to = tris[f + ((e + 1) % 3)];
      if (directed.has(key(to, from))) continue;
      const list = outgoing.get(from);
      if (list) list.push(to);
      else outgoing.set(from, [to]);
    }
  }

  const loops: number[][] = [];
  for (const start of outgoing.keys()) {
    for (;;) {
      const first = outgoing.get(start);
      if (!first || first.length === 0) break;
      const loop = [start];
      let current = first.pop()!;
      let closed = false;
      while (loop.length <= vertexCount) {
        if (current === start) {
          closed = true;
          break;
        }
        loop.push(current);
        const next = outgoing.get(current);
        if (!next || next.length === 0) break;
        current = next.pop()!;
      }
      if (closed && loop.length >= 3) loops.push(loop);
    }
  }
  return loops;
}

/** Of the given border loops, the ones lying entirely on `planeY`. */
function onPlane(
  geometry: BufferGeometry,
  loops: number[][],
  planeY: number,
  tolerance: number,
): number[][] {
  const position = geometry.getAttribute('position') as BufferAttribute;
  return loops.filter((loop) => loop.every((v) => Math.abs(position.getY(v) - planeY) <= tolerance));
}

type PlaneLoop = {
  polygon: Vector2[];
  ids: number[];
  depth: number;
};

/**
 * Drops cavity components that are not inside `outer`, in place, and returns
 * how many went.
 *
 * A size filter is not enough on its own. A cavity is only ever meaningful
 * where the model has material, but the field says "inside" wherever the sign
 * flood decided so, and an isolated pocket of that — a doubled scan layer, a
 * sliver of debris, a spot the flood could not reach — marches into a small
 * blocky lump standing in mid-air next to the jaw (user-reported, visible in
 * Blender as voxel-scale boxes outside the model). Those lumps are perfectly
 * legitimate marching-cubes output; what is wrong is the question, not the
 * answer, so the filter has to ask whether the model encloses them.
 *
 * The test is ray parity along a HORIZONTAL ray. The sheet is open at its base
 * and parity is only valid for a closed surface — but the base opening lies in
 * a horizontal plane, so a horizontal ray can never pass through it and the
 * sheet behaves closed for exactly this question. Several samples per
 * component, majority wins, because a sample can land on the surface itself
 * where the crossing count is a coin toss.
 */
function removeComponentsOutside(
  cavity: BufferGeometry,
  outer: BufferGeometry,
  planeY: number,
  opens: 'up' | 'down',
  cellMm: number,
): number {
  const index = cavity.getIndex()!;
  const position = cavity.getAttribute('position') as BufferAttribute;
  const vertexCount = position.count;

  const parent = new Int32Array(vertexCount);
  for (let v = 0; v < vertexCount; v++) parent[v] = v;
  const find = (v: number): number => {
    let root = v;
    while (parent[root] !== root) root = parent[root];
    while (parent[v] !== root) {
      const next = parent[v];
      parent[v] = root;
      v = next;
    }
    return root;
  };
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  };
  for (let i = 0; i < index.count; i += 3) {
    union(index.getX(i), index.getX(i + 1));
    union(index.getX(i), index.getX(i + 2));
  }

  const members = new Map<number, number[]>();
  for (let i = 0; i < index.count; i += 3) {
    const root = find(index.getX(i));
    const list = members.get(root);
    if (list) list.push(i);
    else members.set(root, [i]);
  }

  // A fresh build: MeshBVH permutes the index buffer it is given, and `outer`
  // goes into the output verbatim.
  const probe = new BufferGeometry();
  probe.setAttribute('position', outer.getAttribute('position'));
  probe.setIndex(new BufferAttribute(Uint32Array.from(outer.getIndex()!.array as ArrayLike<number>), 1));
  const bvh = new MeshBVH(probe);

  const ray = new Ray(new Vector3(), new Vector3(1, 0, 0));
  const point = new Vector3();
  // A sample sitting ON the base plane is the one place a horizontal ray is
  // ambiguous — it grazes the opening's rim rather than crossing a surface —
  // so every sample is pushed a couple of cells into the model first. Without
  // this the filter also took components whose lid lies on the plane, and the
  // base lost 24 mm² of its opening.
  const step = (opens === 'up' ? -2 : 2) * cellMm;
  const enclosed = (v: number): boolean => {
    point.fromBufferAttribute(position, v);
    if (Math.abs(point.y - planeY) < 2 * cellMm) point.y += step;
    let crossings = 0;
    ray.origin.copy(point);
    for (;;) {
      const hit = bvh.raycastFirst(ray, DoubleSide);
      if (!hit) break;
      crossings++;
      // Step past the hit so the next cast cannot find it again.
      ray.origin.copy(hit.point).addScaledVector(ray.direction, 1e-4);
      if (crossings > 64) break;
    }
    return crossings % 2 === 1;
  };

  const drop = new Set<number>();
  for (const [root, faces] of members) {
    const samples = Math.min(faces.length, 5);
    let inside = 0;
    for (let s = 0; s < samples; s++) {
      if (enclosed(index.getX(faces[Math.floor((s * faces.length) / samples)]))) inside++;
    }
    if (inside * 2 <= samples) drop.add(root);
  }
  if (drop.size === 0) return 0;

  const kept: number[] = [];
  for (let i = 0; i < index.count; i += 3) {
    if (drop.has(find(index.getX(i)))) continue;
    kept.push(index.getX(i), index.getX(i + 1), index.getX(i + 2));
  }
  cavity.setIndex(new BufferAttribute(new Uint32Array(kept), 1));
  return drop.size;
}

/** Total area of the faces lying flat on `planeY`. */
function planarFaceArea(geometry: BufferGeometry, planeY: number, tolerance: number): number {
  const position = geometry.getAttribute('position') as BufferAttribute;
  const index = geometry.getIndex()!;
  let total = 0;
  for (let i = 0; i < index.count; i += 3) {
    const a = index.getX(i);
    const b = index.getX(i + 1);
    const c = index.getX(i + 2);
    if (
      Math.abs(position.getY(a) - planeY) > tolerance ||
      Math.abs(position.getY(b) - planeY) > tolerance ||
      Math.abs(position.getY(c) - planeY) > tolerance
    ) {
      continue;
    }
    const ax = position.getX(a);
    const az = position.getZ(a);
    total +=
      Math.abs(
        (position.getX(b) - ax) * (position.getZ(c) - az) -
          (position.getZ(b) - az) * (position.getX(c) - ax),
      ) / 2;
  }
  return total;
}

/**
 * The flat ring that closes the base: the sheet's rim on the outside, the
 * cavity's opening on the inside.
 *
 * Nesting is resolved by containment, because a jaw shell shows both readings
 * at once. The gum skirts down as a tube, so the base already has a buccal
 * contour with a palatal one inside it, and the cavity adds one loop just
 * inside each. Ordered by containment that reads buccal ⊃ cavity ⊃ cavity ⊃
 * palatal, and taking every even depth as a contour with the odd depths inside
 * it as its holes gives the two annular strips the wall actually is.
 */
function triangulateBaseRing(
  loops: PlaneLoop[],
  outwardY: number,
  positionOf: (id: number) => [number, number],
): { triangles: number[]; report: string[]; facesOutward: boolean; areaMm2: number } {
  for (let i = 0; i < loops.length; i++) {
    for (let j = 0; j < loops.length; j++) {
      if (i !== j && enclosedBy(loops[j].polygon, loops[i].polygon)) loops[i].depth++;
    }
  }

  const triangles: number[] = [];
  const groups: string[] = [];
  for (const contour of loops) {
    if (contour.depth % 2 !== 0) continue;
    const holes = loops.filter(
      (hole) =>
        hole !== contour &&
        hole.depth === contour.depth + 1 &&
        enclosedBy(contour.polygon, hole.polygon),
    );

    // Bridge each hole into the contour by hand and triangulate ONE slit
    // polygon, rather than handing earcut a contour + holes.
    //
    // Earcut eliminates holes itself, but by a VISIBILITY test: it shoots a ray
    // from the hole's leftmost point and needs an unobstructed outer edge to
    // bridge to. Where two rims run close together or touch — which the buccal
    // and palatal rings do at the distal ends — that search comes back empty
    // and the hole is SILENTLY SKIPPED. Measured on the captured jaw: a
    // 233-vertex contour with a 554-vertex hole came back as 231 triangles,
    // i.e. the contour alone, so the ring covered the palate as solid material
    // and left all 554 of the hole's edges open.
    //
    // Bridging on the CLOSEST PAIR instead cannot fail: the shortest segment
    // between two disjoint loops never crosses either of them.
    let points = contour.polygon.slice();
    let ids = contour.ids.slice();
    for (const hole of holes) {
      const spliced = spliceHole(points, ids, hole);
      points = spliced.points;
      ids = spliced.ids;
    }
    const faces = ShapeUtils.triangulateShape(points, []);

    const from = triangles.length;
    // Degenerate triangles along the bridge seam are KEPT: a sliver (a, b, a)
    // contributes a→b and b→a and cancels itself, whereas dropping it leaves
    // both of its real edges unpartnered.
    for (const [a, b, c] of faces) triangles.push(ids[a], ids[b], ids[c]);
    groups.push(
      `${contour.ids.length}v + ${holes.length}h(${holes
        .map((h) => h.ids.length)
        .join('/')}) → ${faces.length}t`,
    );

    // Which way round the ring goes is decided AGAINST THE SHEET'S OWN RIM, not
    // from the plane's outward direction. The border rule is the same one the
    // skirt and the interproximal bands follow — the sheet walks a→b, whatever
    // closes it contains b→a — and reading it off the rim works whichever way
    // the loop happens to run and whichever way the sheet is oriented. Deriving
    // it from the outward normal instead only works when both agree, and on the
    // captured jaw they do not: the rim there had a negative area, which
    // flipped the whole ring and left all 609 of its edges open.
    const first = contour.ids[0];
    const second = contour.ids[1];
    let followsRim = false;
    for (let t = from; t < triangles.length && !followsRim; t += 3) {
      for (let e = 0; e < 3; e++) {
        if (triangles[t + e] === first && triangles[t + ((e + 1) % 3)] === second) {
          followsRim = true;
          break;
        }
      }
    }
    if (followsRim) {
      for (let t = from; t < triangles.length; t += 3) {
        const swap = triangles[t + 1];
        triangles[t + 1] = triangles[t + 2];
        triangles[t + 2] = swap;
      }
    }
  }

  let areaMm2 = 0;
  for (let t = 0; t < triangles.length; t += 3) {
    const [ax, az] = positionOf(triangles[t]);
    const [bx, bz] = positionOf(triangles[t + 1]);
    const [cx, cz] = positionOf(triangles[t + 2]);
    areaMm2 += Math.abs((bx - ax) * (cz - az) - (bz - az) * (cx - ax)) / 2;
  }

  // The rim decided the winding, so the outward direction is now something to
  // CHECK rather than something to impose: if the ring ends up facing into the
  // model, the sheet it closes is inside-out and the body will read as one.
  let facesOutward = true;
  if (triangles.length >= 3) {
    const [ax, az] = positionOf(triangles[0]);
    const [bx, bz] = positionOf(triangles[1]);
    const [cx, cz] = positionOf(triangles[2]);
    // A triangle wound counter-clockwise in (x, z) has its normal along −Y.
    facesOutward = -((bx - ax) * (cz - az) - (bz - az) * (cx - ax)) * outwardY > 0;
  }

  const report = loops
    .map((loop) => `${loop.ids.length}v d${loop.depth} ${signedArea(loop.polygon).toFixed(2)}mm²`)
    .concat(groups);
  return { triangles, report, facesOutward, areaMm2 };
}

/**
 * Cuts `hole` into `points`/`ids` along the shortest segment joining them,
 * leaving one slit polygon. The hole is walked in its own direction, which is
 * already opposite to the contour's — that is what made it read as a hole.
 */
function spliceHole(
  points: Vector2[],
  ids: number[],
  hole: PlaneLoop,
): { points: Vector2[]; ids: number[] } {
  let bestOuter = 0;
  let bestInner = 0;
  let best = Infinity;
  for (let i = 0; i < points.length; i++) {
    for (let j = 0; j < hole.polygon.length; j++) {
      const dx = points[i].x - hole.polygon[j].x;
      const dz = points[i].y - hole.polygon[j].y;
      const distance = dx * dx + dz * dz;
      if (distance < best) {
        best = distance;
        bestOuter = i;
        bestInner = j;
      }
    }
  }

  const nextPoints = points.slice(0, bestOuter + 1);
  const nextIds = ids.slice(0, bestOuter + 1);
  for (let k = 0; k <= hole.polygon.length; k++) {
    const at = (bestInner + k) % hole.polygon.length;
    nextPoints.push(hole.polygon[at]);
    nextIds.push(hole.ids[at]);
  }
  nextPoints.push(points[bestOuter]);
  nextIds.push(ids[bestOuter]);
  for (let k = bestOuter + 1; k < points.length; k++) {
    nextPoints.push(points[k]);
    nextIds.push(ids[k]);
  }
  return { points: nextPoints, ids: nextIds };
}

function signedArea(polygon: Vector2[]): number {
  let total = 0;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    total += (polygon[j].x - polygon[i].x) * (polygon[j].y + polygon[i].y);
  }
  return total / 2;
}

/**
 * Whether `inner` lies inside `outer`, decided by a MAJORITY of sample points
 * rather than by one.
 *
 * A single-point test is what the first version used and it is not good enough
 * here: the sheet's rim is a decimated polyline while the cavity's opening
 * follows the field, so a loop that is plainly inside can still have individual
 * vertices land a hair outside. One such vertex read as depth 0, the loop was
 * promoted to its own contour, and the base came back with 1969 open edges and
 * more area than the footprint has.
 */
function enclosedBy(outer: Vector2[], inner: Vector2[]): boolean {
  const samples = Math.min(inner.length, 11);
  let inside = 0;
  for (let s = 0; s < samples; s++) {
    if (contains(outer, inner[Math.floor((s * inner.length) / samples)])) inside++;
  }
  return inside * 2 > samples;
}

/** Ray crossing test; `polygon` uses x for world X and y for world Z. */
function contains(polygon: Vector2[], point: Vector2): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const zi = polygon[i].y;
    const xj = polygon[j].x;
    const zj = polygon[j].y;
    if (zi > point.y !== zj > point.y && point.x < ((xj - xi) * (point.y - zi)) / (zj - zi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/** Enclosed volume by the divergence theorem; negative means inward-facing. */
export function signedVolume(geometry: BufferGeometry): number {
  const position = geometry.getAttribute('position') as BufferAttribute;
  const index = geometry.getIndex()!;
  let total = 0;
  for (let i = 0; i < index.count; i += 3) {
    const a = index.getX(i);
    const b = index.getX(i + 1);
    const c = index.getX(i + 2);
    const ax = position.getX(a);
    const ay = position.getY(a);
    const az = position.getZ(a);
    const bx = position.getX(b);
    const by = position.getY(b);
    const bz = position.getZ(b);
    const cx = position.getX(c);
    const cy = position.getY(c);
    const cz = position.getZ(c);
    total += ax * (by * cz - bz * cy) + ay * (bz * cx - bx * cz) + az * (bx * cy - by * cx);
  }
  return total / 6;
}

/** Swaps two corners of every triangle, in place. */
function reverseWinding(geometry: BufferGeometry): void {
  const index = geometry.getIndex()!;
  const array = index.array as Uint32Array;
  for (let f = 0; f < array.length; f += 3) {
    const swap = array[f + 1];
    array[f + 1] = array[f + 2];
    array[f + 2] = swap;
  }
  index.needsUpdate = true;
}

/**
 * Outer sheet + its cap + the cavity, into one indexed buffer. Done by hand
 * rather than through a merge-and-weld: the cap's triangles already reference
 * the sheet's own vertices, and the cavity must NOT be welded to anything —
 * it is a separate closed shell and welding it would be a chance to fuse it to
 * a wall it merely comes close to.
 */
function concatenate(
  outer: BufferGeometry,
  capTriangles: number[],
  cavity: BufferGeometry,
): BufferGeometry {
  const outerPositions = outer.getAttribute('position') as BufferAttribute;
  const outerIndex = outer.getIndex()!;
  const cavityPositions = cavity.getAttribute('position') as BufferAttribute;
  const cavityIndex = cavity.getIndex()!;
  const outerVertexCount = outerPositions.count;

  const positions = new Float32Array((outerVertexCount + cavityPositions.count) * 3);
  for (let v = 0; v < outerVertexCount; v++) {
    positions[v * 3] = outerPositions.getX(v);
    positions[v * 3 + 1] = outerPositions.getY(v);
    positions[v * 3 + 2] = outerPositions.getZ(v);
  }
  for (let v = 0; v < cavityPositions.count; v++) {
    const at = (outerVertexCount + v) * 3;
    positions[at] = cavityPositions.getX(v);
    positions[at + 1] = cavityPositions.getY(v);
    positions[at + 2] = cavityPositions.getZ(v);
  }

  const index = new Uint32Array(outerIndex.count + capTriangles.length + cavityIndex.count);
  let at = 0;
  for (let i = 0; i < outerIndex.count; i++) index[at++] = outerIndex.getX(i);
  for (const v of capTriangles) index[at++] = v;
  for (let i = 0; i < cavityIndex.count; i++) index[at++] = cavityIndex.getX(i) + outerVertexCount;

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setIndex(new BufferAttribute(index, 1));
  return geometry;
}
